import { useEffect, useState } from "react";
import { ShieldCheck, Smartphone, KeyRound, Download, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AdminTitle } from "@/admin/AdminLayout";
import { Field } from "@/admin/FormDialog";

const Card = ({ icon: Icon, title, children, testid }) => (
  <section data-testid={testid} className="vellum p-6">
    <p className="font-heading font-bold flex items-center gap-2 mb-4"><Icon className="w-5 h-5 text-[#991b1b]" /> {title}</p>
    {children}
  </section>
);

const download = (name, text, type = "text/plain") => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
};

const CodesBox = ({ codes }) => (
  <div data-testid="recovery-codes-box" className="mt-4 border-2 border-[#991b1b] bg-[#fff8ec] p-4">
    <p className="text-sm font-bold text-[#991b1b] mb-2">Save these now — they will not be shown again. Each code works once.</p>
    <div className="grid grid-cols-2 gap-2 font-mono text-sm">{codes.map((c) => <span key={c} data-testid="recovery-code">{c}</span>)}</div>
    <div className="flex gap-2 mt-3">
      <button type="button" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Copied"); }} className="btn-ghost-illum !py-2"><Copy className="w-4 h-4" /> Copy</button>
      <button type="button" data-testid="recovery-codes-download" onClick={() => download("elirios-recovery-codes.txt", `Lorekeepers of Elirios — admin recovery codes\n\n${codes.join("\n")}\n`)} className="btn-ghost-illum !py-2"><Download className="w-4 h-4" /> Download</button>
    </div>
  </div>
);

const AccountCard = ({ s, reload }) => {
  const [f, setF] = useState({ name: s.name, email: s.email, notify_email: s.notify_email || "", new_password: "", confirm: "", current_password: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (f.new_password && f.new_password !== f.confirm) return toast.error("New passwords do not match");
    try { await api.put("/admin/account", f); toast.success("Login details updated"); setF((p) => ({ ...p, new_password: "", confirm: "", current_password: "" })); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card icon={KeyRound} title="Admin login details" testid="account-card">
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
        <Field label="Display name"><input data-testid="account-name-input" value={f.name} onChange={set("name")} className="admin-input" /></Field>
        <Field label="Login email"><input type="email" data-testid="account-email-input" value={f.email} onChange={set("email")} className="admin-input" /></Field>
        <Field label="New password (leave blank to keep)"><input type="password" data-testid="account-new-password" value={f.new_password} onChange={set("new_password")} className="admin-input" /></Field>
        <Field label="Confirm new password"><input type="password" data-testid="account-confirm-password" value={f.confirm} onChange={set("confirm")} className="admin-input" /></Field>
        <div className="sm:col-span-2">
          <Field label="Notification email (where reader-submission alerts are sent; blank = login email)">
            <input type="email" data-testid="account-notify-email" value={f.notify_email} onChange={set("notify_email")} placeholder={f.email} className="admin-input" />
          </Field>
        </div>
        <div className="sm:col-span-2 grid sm:grid-cols-[1fr_auto] gap-4 items-end border-t border-[#c9a227]/40 pt-4">
          <Field label="Current password (required to save)"><input type="password" required data-testid="account-current-password" value={f.current_password} onChange={set("current_password")} className="admin-input" /></Field>
          <button data-testid="account-save-button" className="btn-illum theme-flame">Save login details</button>
        </div>
      </form>
    </Card>
  );
};

const AuthenticatorCard = ({ s, reload, onCodes }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const start = () => run(async () => { setSetup((await api.post("/admin/totp/enroll")).data); setCode(""); });
  const confirm = () => run(async () => {
    const { data } = await api.post("/admin/totp/confirm", { code });
    toast.success("Authenticator app linked — 2-FA is on"); setSetup(null); reload();
    if (data.recovery_codes) onCodes(data.recovery_codes);
  });
  const disable = () => { const password = window.prompt("Enter your password to unlink the authenticator app"); if (password) run(async () => { await api.post("/admin/totp/disable", { password }); toast.success("Authenticator unlinked — 2-FA is off"); reload(); }); };

  return (
    <Card icon={Smartphone} title="Two-factor sign-in (Microsoft or Google Authenticator)" testid="totp-card">
      <p data-testid="totp-status" className={`font-heading text-sm mb-4 ${s.totp_enabled ? "text-[#2f6b3a]" : "text-[#991b1b]"}`}>
        {s.totp_enabled ? "ON — each Admin Login asks for the 6-digit code from your authenticator app" : "OFF — only your password protects the Study"}
      </p>
      {!s.totp_enabled && !setup && <button type="button" onClick={start} disabled={busy} data-testid="totp-start" className="btn-illum theme-frost">Link authenticator app</button>}
      {setup && (
        <div data-testid="totp-setup" className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="bg-white p-3 border-2 border-[#c9a227] w-fit"><QRCodeSVG value={setup.otpauth_uri} size={190} data-testid="totp-qr" /></div>
          <div>
            <ol className="list-decimal pl-5 space-y-1.5 text-[15px]">
              <li>Install <b>Microsoft Authenticator</b> or <b>Google Authenticator</b> on your phone.</li>
              <li>Tap <b>+</b> → <b>Scan a QR code</b> (Microsoft: “Other account”), and scan this code.</li>
              <li>Type the 6-digit code it shows below and press Verify.</li>
            </ol>
            <p className="text-xs mt-3 text-[#5e4a35]">Can't scan? Enter this key manually: <code data-testid="totp-secret" className="font-mono break-all">{setup.secret}</code></p>
            <div className="flex gap-3 mt-4 items-end">
              <input data-testid="totp-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="admin-input !w-40 font-mono tracking-widest" />
              <button type="button" onClick={confirm} disabled={busy || code.length !== 6} data-testid="totp-confirm" className="btn-illum theme-flame disabled:opacity-50">Verify & turn on</button>
            </div>
          </div>
        </div>
      )}
      {s.totp_enabled && (
        <p className="text-sm">Got a new phone? Click <button type="button" onClick={disable} data-testid="totp-disable" className="text-[#991b1b] underline">unlink</button>, then link the new phone. Lost the phone? Sign in with a recovery code first.</p>
      )}
    </Card>
  );
};

const RecoveryCard = ({ s, reload, codes, onCodes }) => {
  const regen = async () => {
    const password = window.prompt("Enter your password to create new recovery codes (old ones stop working)");
    if (!password) return;
    try { const { data } = await api.post("/admin/recovery-codes", { password }); onCodes(data.recovery_codes); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const backup = async () => {
    try { const { data } = await api.get("/admin/backup", { responseType: "text", transformResponse: (r) => r }); download(`elirios-backup-${new Date().toISOString().slice(0, 10)}.json`, data, "application/json"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card icon={ShieldCheck} title="Recovery & backup" testid="recovery-card">
      <p className="text-[15px] leading-relaxed">Recovery codes let you back in if you <b>forget your password</b>, <b>lose your phone</b> or <b>change your mobile number</b>. On the Admin Login page choose “Lost password or phone?”, enter your email and one code. Keep them somewhere only you can reach (a printed sheet, a password manager).</p>
      <p data-testid="recovery-remaining" className="font-heading text-sm mt-3">{s.recovery_codes_remaining} unused recovery codes</p>
      <div className="flex flex-wrap gap-3 mt-4">
        <button type="button" onClick={regen} data-testid="recovery-generate" className="btn-illum theme-flame">{s.recovery_codes_remaining ? "Generate new codes" : "Create recovery codes"}</button>
        <button type="button" onClick={backup} data-testid="backup-download" className="btn-ghost-illum"><Download className="w-4 h-4" /> Download full data backup</button>
      </div>
      {codes && <CodesBox codes={codes} />}
      <p className="text-xs italic mt-4 text-[#5e4a35]">The data backup is a JSON file with all series, books, sections, articles, settings and accounts (passwords excluded). Images stay in your storage and are referenced by link.</p>
    </Card>
  );
};

export default function AdminSecurity() {
  const [s, setS] = useState(null);
  const [codes, setCodes] = useState(null);
  const load = () => api.get("/admin/security").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return null;
  return (
    <div data-testid="admin-security" className="grid gap-6 max-w-4xl">
      <AdminTitle eyebrow="Before you go live" title="Account & Security" />
      <AccountCard s={s} reload={load} />
      <AuthenticatorCard s={s} reload={load} onCodes={setCodes} />
      <RecoveryCard s={s} reload={load} codes={codes} onCodes={setCodes} />
    </div>
  );
}
